import './types/types.js';
import '../node_modules/@lfi/jsonstat-js/dist/types.js';
import { print } from './gui/print.js';
import { panel } from './gui/panel.js';
import { fhr } from './utils/fhr.js';
import { dataSelector } from './gui/dataSelector.js';
import { eventBus } from './eventBus.js';
import { legend } from './gui/legend.js';
import { header } from './gui/header.js';
import { i18n } from './i18n.js';
import { JsonStatReader } from './JsonStatReader.js';
import { map } from './map.js';
import { table } from './gui/table.js';
import { svgExport } from './svgExport.js';
import { dataSeries, RANGE_MIXED } from './dataSeries.js';
import { byId } from './utils/dom.js';
import { classification } from './gui/classification.js';
import { color } from './gui/color.js';
import { array } from '../node_modules/@lfi/jsonstat-js/dist/array.js';
import { spinner } from './gui/spinner.js';
import { label } from './layers/label.js';
import { circle } from './layers/circle.js';

const resultmap = {

  /**
   * @property {URLSearchParams}
   */
  query: new URLSearchParams(window.location.search),

  /**
   * @type {JsonStatReader} NAFIDAS JSON-stat reader
   */
  reader: null,

  /**
   * @type {?DataSeries}
   */
  rowData: null,

  /**
   * What type was the previously selected range of the data series?
   */
  _previousRange: null,

  /**
   * Initialize the app.
   * @param {JsonStat} jsonStat
   * @param {String} tableHTML
   * @returns {void}
   */
  async init (jsonStat, tableHTML) {
    const lang = i18n.getLang();
    this.reader = new JsonStatReader(jsonStat, lang);

    dataSeries.init(this.reader);

    let rowIdx = this._initRowIdx(this.reader);
    let circleLayer, rowData = dataSeries.get(rowIdx);

    await Promise.all([
      i18n.init(lang),
      label.init(this.reader, rowData)
    ]);
    this.label = label;

    legend.init(this.reader);
    dataSelector.init(this.reader, rowIdx);
    table.init(tableHTML);
    svgExport.init(this.reader);
    if (this.reader.isChoropleth()) {
      await classification.init(this.reader, rowData);
    } else {
      byId('viewSetting-label').checked = false;
      circleLayer = circle.init(this._reader, this.label.labelPoints, rowData);
      byId('viewSetting-palette')
        .closest('div')
        .classList.add('hidden');
    }

    header.render(this.reader, rowIdx, tableHTML);

    // Note: the browser might remember the setting of form elements,
    //    so we have to initialize accordingly.
    //    This has to be done after this._initEvents() to update the legend and the map accordingly.
    this.updateData(rowData);

    legend.update(rowData);
    await map.init(this.reader, rowData);
    map.map.addControl(table.control);
    if (circleLayer) {
      map.map.addLayer(circleLayer.layer);
    }
    print.init(map);
    panel.init(map);
    this._initLayerVisibility();
    this._initEvents();
    this._fadeIn();
    if (!this.isCssMediaMobile()) {
      dataSelector.scrollToSelectedItem();
    }
    this.rowData = rowData;
  },

  /**
   * Initialize the event listeners on the event-bus
   * @listens eventBus~event:rowSelect
   * @listens eventBus~event:gradientChange
   * @returns {void}
   * @private
   */
  _initEvents () {
    eventBus.add('rowSelect', this.update.bind(this));
    eventBus.add('gradientChange', this.updateColor.bind(this));
    byId('viewSetting-relief').addEventListener('change', evt => map.relief.layer.setVisible(evt.target.checked));
    byId('viewSetting-water').addEventListener('change', evt => map.water.layer.setVisible(evt.target.checked));
    byId('viewSetting-label').addEventListener('change', evt => this.label.layer.setVisible(evt.target.checked));
    byId('viewSetting-table').addEventListener('click', () => eventBus.dispatch('toggleTable'));
    byId('view-export').addEventListener('click', this._download.bind(this));
    window.addEventListener('popstate', this._handleHistory.bind(this));
  },

  /**
   * Update the app.
   * Updates the gui, map, legends and data series.
   * @param {RowSelectEvent|GradientChangeEvent|PopStateEvent} evt
   */
  update (evt) {
    const rowIdx = evt.state ? evt.state.rowIdx : evt.detail.rowIdx;
    const rowData = dataSeries.get(rowIdx);
    if (this.reader.isChoropleth()) {
      if (rowData.range !== this._previousRange || rowData.range === RANGE_MIXED) {
        // note: We have to update the gradients when the range type is mixed,
        //  because then, the domain (zero point) generally shifts with each new data series.
        classification.updateGradientContinuous(rowData);
        classification.updateGradientClassified(rowData);
      }
      classification.updateGradientDiscrete(rowData);
    }
    this.updateData(rowData);
    legend.update(rowData);
    map.update(rowData);
    if (evt.type !== 'popstate') {
      // do not set the history again, when coming from the history popstate event
      this._updateHistory(rowIdx);
    }
    this.rowData = rowData;
    this._previousRange = rowData.range;
  },

  /**
   * Update the color (and radius) of the data series.
   * @param {DataSeries} rowData
   */
  updateData (rowData) {
    let gradient;
    if (this.reader.isChoropleth()) {
      gradient = classification.getGradient().scale;
    } else {
      gradient = color.getSingle(rowData);
      dataSeries.addRadius(rowData);
    }
    dataSeries.addColor(rowData, gradient);
  },

  /**
   * Update the color of the data series, map and legend.
   * Note: Does not update the color gradients.
   */
  updateColor () {
    this.updateData(this.rowData);
    legend.update(this.rowData);
    map.update(this.rowData);
  },

  /**
   * Update the rowIdx parameter of the browser history.
   * Updates the application's query (string) property and the browser history.
   * @param {Number} rowIdx row index
   * @param replace replace the url in the history instead of adding it to the stack
   * @returns {void}
   * @private
   */
  _updateHistory (rowIdx, replace = false) {
    this.query.set('rowIdx', rowIdx);
    const newUrl = window.location.pathname + '?' + this.query.toString();
    const data = { rowIdx: rowIdx };
    if (replace) {
      window.history.replaceState(data, '', newUrl);
    } else {
      window.history.pushState(data, '', newUrl);
    }
  },

  /**
   * Handle the popstate event.
   * Set the state of the application according to the browser history (back/forward button).
   * @param {PopStateEvent} evt
   * @private
   */
  _handleHistory (evt) {
    dataSelector.highlightRow(evt.state.rowIdx);
    if (!this.isCssMediaMobile()) {
      dataSelector.scrollToSelectedItem();
    }
    this.update(evt);
  },

  /**
   * Set the browser history.
   * @private
   */
  _initRowIdx (reader) {
    let replace = false,
      rowIdx = this.query.has('rowIdx') ? parseInt(this.query.get('rowIdx')) : null;

    if (rowIdx === null) {
      rowIdx = this.getRowIdxDefault(reader);
      replace = true;
    }
    this._updateHistory(rowIdx, replace);

    return rowIdx;
  },

  /**
   * Download the exported SVG.
   * @return {Promise<void>}
   * @private
   */
  async _download () {
    const blob = await svgExport.export(map, legend, header, map.copyright, this.rowData);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'map.svg';
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    document.body.removeChild(link);
  },

  /**
   * Fade the application in.
   * @returns {void}
   * @private
   */
  _fadeIn () {
    document.querySelector('.container.container_invisible').classList.remove('container_invisible');
  },

  /**
   * Is the css media query mobile breakpoint reached ?
   * @returns {boolean}
   */
  isCssMediaMobile () {
    return window.getComputedStyle(document.body, '::after').content.includes('mobile');
  },

  /**
   * Set the initial visibility of the layers.
   * @private
   */
  _initLayerVisibility () {
    let el;
    el = byId('viewSetting-relief');
    map.relief.layer.setVisible(el.checked);
    el = byId('viewSetting-water');
    map.water.layer.setVisible(el.checked);
    el = byId('viewSetting-label');
    this.label.layer.setVisible(el.checked);
  },

  /**
   * Return the default row index.
   * @param {JsonStatReader} reader
   */
  getRowIdxDefault (reader) {
    const nullrows = reader.jsonStat.extension.rendering.nullrows,
      shape = reader.getDimSizes(),
      id = reader.getDimIdOthers().pop(),
      dimIdx = reader.jsonStat.id.indexOf(id),
      stride = array.stride(shape)[dimIdx];

    let idx = 0;
    if (nullrows) {
      let i = 0;
      // Find the first row, which is not a nullrow.
      while (typeof nullrows[i] !== 'undefined' && nullrows[i] === i && idx < 1000) {
        i += stride;
        idx += 1;
      }
    }

    return idx;
  }
};
const dummy = spinner;  // this prevents Minify from removing the unused import/export

export { resultmap, map, fhr, JsonStatReader, spinner };